import { useState, useRef, useEffect } from "react";

import {
  Check,
  Close,
  ExpandLess,
  ExpandMore,
  PauseCircle,
  PlayCircle,
  Fullscreen,
  HelpCenterOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Skeleton,
  CircularProgress,
  Avatar,
  Grid,
  Card,
  LinearProgress,
  Tabs,
  Tab,
  Modal,
} from "@mui/material";
import { useInView } from "react-intersection-observer";

import QnaRating from "./QnaRating";
import SegmentedProgressBar from "./SegmentedProgressBar";
import Logo from "../assets/images/app-logo-single.webp";
import { getCamelCaseWord, metricColors } from "../utils/functions";

const VideoLoadingState = () => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "rgba(0, 0, 0, 0.7)",
      color: "white",
      gap: 2,
      zIndex: 10, // Add this to ensure it appears on top
    }}
  >
    <CircularProgress size={40} thickness={4} sx={{ color: "white" }} />
    <Typography variant="body2" sx={{ color: "white", fontFamily: "Outfit" }}>
      Loading video...
    </Typography>
  </Box>
);

const VideoPlaceholder = () => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "grey.900",
      color: "white",
      gap: 2,
    }}
  >
    <PlayCircle sx={{ fontSize: 48, opacity: 0.8 }} />
    <Typography variant="body2" sx={{ opacity: 0.8, fontFamily: "Outfit" }}>
      Video will load when visible
    </Typography>
  </Box>
);

const VideoPlayer2 = ({
  videoUrl,
  transcription,
  expanded,
  followUpQnA,
  qna,
  language,
  selectedAssessment,
  shouldUseLTR,
}) => {
  const allQnaMessages =
    selectedAssessment?.questions?.map((q) => q?.message) || [];
  const isArabic = language === "Arabic"; // Check for Arabic language
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isModalPlaying, setIsModalPlaying] = useState(false);

  const [showControls, setShowControls] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [modalTabIndex, setModalTabIndex] = useState(1); // Separate tab state for modal

  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);
  // Convert startTime to Date object
  const startTimestamp = new Date(qna?.startTime).getTime();

  const { ref: videoContainerRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = videoUrl; // Set new video URL
      videoRef.current.load();
      setVideoLoaded(false);
      setIsLoading(true);
    }
  }, [videoUrl]); // Ensure effect runs when videoUrl changes

  useEffect(() => {
    if (videoRef.current && inView && expanded) {
      setIsLoading(true);
      videoRef.current.load();
    }
  }, [inView, expanded]);

  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
    };
  }, [expanded]);

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    setIsLoading(false);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMouseEnter = () => {
    setShowControls(true);
  };

  const handleMouseLeave = () => {
    setShowControls(false);
  };

  const handleFullscreenClick = () => {
    setFullscreenOpen(true);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
    if (modalVideoRef.current) {
      modalVideoRef.current.pause();
      setIsModalPlaying(false);
    }
  };

  const handleModalPlayPause = () => {
    if (modalVideoRef.current) {
      if (isModalPlaying) {
        modalVideoRef.current.pause();
      } else {
        modalVideoRef.current.play();
      }
      setIsModalPlaying(!isModalPlaying);
    }
  };

  return (
    <>
      {/* Main Video Player */}
      <Box
        sx={{
          display: "flex",
          border: "2px solid #E0E0E0",
          borderRadius: 3,
          overflow: "hidden",
          maxWidth: "100%",
          height: "400px", // Add a fixed height for the entire component
          mx: "auto",
        }}
      >
        {/* Left: Video Section */}
        <Box
          ref={videoContainerRef}
          sx={{
            width: "50%",
            height: "100%",
            bgcolor: "black",
            position: "relative",
            overflow: "hidden",
            borderRadius: 3,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {inView && expanded ? (
            <>
              {/* IMPORTANT: Place the loading state BEFORE the video element */}
              {isLoading && !videoLoaded && <VideoLoadingState />}

              <video
                key={videoUrl} // This ensures the video resets when the URL changes
                ref={videoRef}
                style={{
                  width: "100%",
                  height: "100%",
                  // display: videoLoaded ? "block" : "none",
                  objectFit: "cover",
                }}
                preload="none"
                onLoadedData={handleVideoLoaded}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>

              {/* Play/Pause Button Overlay */}
              {videoLoaded && (
                <IconButton
                  onClick={handlePlayPause}
                  disableFocusRipple
                  disableTouchRipple
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display:
                      (showControls && isPlaying) || !isPlaying
                        ? "flex"
                        : "none",
                    transition: "opacity 0.3s",
                    opacity:
                      (showControls && isPlaying) || !isPlaying ? 0.8 : 0,
                    "&:hover": {
                      opacity: 1,
                      bgcolor: "transparent",
                    },
                    padding: 1,
                  }}
                >
                  {isPlaying ? (
                    <PauseCircle sx={{ fontSize: 64, color: "white" }} />
                  ) : (
                    <PlayCircle sx={{ fontSize: 64, color: "white" }} />
                  )}
                </IconButton>
              )}
            </>
          ) : (
            <VideoPlaceholder />
          )}

          {/* Fullscreen Button */}
          <IconButton
            onClick={handleFullscreenClick}
            disableFocusRipple
            disableTouchRipple
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: showControls || !isPlaying ? "flex" : "none",
              transition: "opacity 0.3s",
              opacity: showControls || !isPlaying ? 0.8 : 0,
              "&:hover": {
                opacity: 1,
                bgcolor: "transparent",
              },
            }}
          >
            <Fullscreen sx={{ color: "white", fontSize: 50 }} />
          </IconButton>
        </Box>

        {/* Right: Transcript Section */}
        <Box sx={{ width: "50%", p: 2, bgcolor: "#F9FAFB" }}>
          <Tabs
            value={tabIndex}
            onChange={(e, newValue) => setTabIndex(newValue)}
            sx={{
              "& .MuiTab-root": {
                pb: 1.5,
                fontFamily: "Outfit",
                fontSize: { xs: "12px", sm: "15px", md: "16px" },
                "&.Mui-selected": {
                  color: "#9048FB",
                  fontFamily: "Outfit",
                },
              },
              "& .MuiTabs-indicator": {
                bgcolor: "#9048FB",
              },
            }}
          >
            <Tab
              label="Follow-up Q&A"
              sx={{ textTransform: "none", fontSize: 14 }}
            />
            <Tab
              label="Transcript"
              sx={{ textTransform: "none", fontSize: 14 }}
            />
          </Tabs>

          {/* Follow-up Q&A Tab */}
          {tabIndex === 0 && (
            <Box
              sx={{
                mt: 1,
                display: "flex",
                flexDirection: "column",
                height:
                  followUpQnA && followUpQnA?.length > 0 ? "300px" : "auto",
                overflowY: "auto",
                gap: 2,
                p: 1,
              }}
            >
              {followUpQnA && followUpQnA?.length > 0 ? (
                followUpQnA
                  .filter((item) => !allQnaMessages.includes(item.question)) // 👈 filter out matching questions
                  .map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "row", // Adjust layout to row for question and answer
                        alignItems: "flex-end",
                        gap: 3, // Adjust the gap between question and answer
                        borderBottom: "1px solid #e0e0e0", // Border between each QnA block
                        pb: 2,
                        "&:last-child": {
                          borderBottom: "none", // Remove border for last item
                        },
                      }}
                    >
                      {/* Question */}
                      {item.question && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flex: 1, // Allow the question to take up available space
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "black",
                              width: 30,
                              height: 30,
                              fontSize: "0.85rem",
                            }}
                          >
                            Q
                          </Avatar>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "0.85rem",
                              color: "#5A6E7E",
                              direction: !shouldUseLTR ? "rtl" : "ltr",
                              textAlign: !shouldUseLTR ? "right" : "left",
                            }}
                          >
                            {item.question}
                          </Typography>
                        </Box>
                      )}

                      {/* Answer */}
                      {item.answer && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "#FFB400",
                              width: 30,
                              height: 30,
                              fontSize: "0.85rem",
                            }}
                          >
                            CR
                          </Avatar>

                          <Typography
                            variant="body2"
                            sx={{
                              color: "#5A6E7E",
                              fontSize: "0.85rem",
                              direction: !shouldUseLTR ? "rtl" : "ltr",
                              textAlign: !shouldUseLTR ? "right" : "left",
                            }}
                          >
                            {item.answer}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))
              ) : (
                <Typography
                  variant="body1"
                  sx={{ color: "gray", textAlign: "center" }}
                >
                  No follow-up questions and answers found.
                </Typography>
              )}
            </Box>
          )}

          {/* Transcript Tab */}
          {tabIndex === 1 && (
            <Box sx={{ mt: 1, height: "300px", overflowY: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column", // Stack items vertically
                  py: 1,
                  gap: 1,
                }}
              >
                {/* Check if transcription exists */}
                {followUpQnA && followUpQnA?.length > 0 ? (
                  followUpQnA
                    ?.filter((item) => item.answer) // Show only answers
                    .map((item, index) => {
                      const itemTime = new Date(item.time).getTime();
                      const baseTime = new Date(startTimestamp).getTime();
                      const elapsedSeconds = Math.round(
                        (itemTime - baseTime) / 1000
                      );
                      const minutes = String(
                        Math.floor(elapsedSeconds / 60)
                      ).padStart(2, "0");
                      const seconds = String(elapsedSeconds % 60).padStart(
                        2,
                        "0"
                      );

                      return (
                        <Box
                          key={index}
                          sx={{
                            mt: 1,
                            mb: 1,
                            p: 1,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          {/* Timestamp */}
                          <Typography
                            variant="caption"
                            sx={{
                              color: "gray",
                              fontSize: "0.85rem",
                              width: "40px",
                              textAlign: "right",
                              flexShrink: 0,
                            }}
                          >
                            {minutes}:{seconds}
                          </Typography>

                          {/* Answer Text */}
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#2F4150",
                              fontSize: "0.85rem",
                              ml: 1,
                              flexGrow: 1,
                              direction: !shouldUseLTR ? "rtl" : "ltr",
                              textAlign: !shouldUseLTR ? "right" : "left",
                            }}
                          >
                            {item.answer}
                          </Typography>
                        </Box>
                      );
                    })
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: "gray", textAlign: "center" }}
                  >
                    No transcription found
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Fullscreen Modal - Revised to match the provided image */}
      <Modal
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        aria-labelledby="fullscreen-video-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
            display: "flex",
            outline: "none",
          }}
        >
          {/* Left side: Video */}
          <Box
            sx={{
              width: "70%", // Adjust based on the right panel width
              height: "100%",
              position: "relative",
              bgcolor: "black",
              overflow: "hidden",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <video
              ref={modalVideoRef}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              controls
              preload="auto"
              onPlay={() => setIsModalPlaying(true)}
              onPause={() => setIsModalPlaying(false)}
              onEnded={() => setIsModalPlaying(false)}
            >
              <source src={videoUrl} type="video/mp4" />
            </video>

            {/* Play/Pause button for modal */}
            <IconButton
              onClick={handleModalPlayPause}
              disableFocusRipple
              disableTouchRipple
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: showControls || !isModalPlaying ? "flex" : "none",
                opacity: 0.8,
                "&:hover": {
                  opacity: 1,
                  bgcolor: "transparent",
                },
              }}
            >
              {isModalPlaying ? (
                <PauseCircle sx={{ fontSize: 80, color: "white" }} />
              ) : (
                <PlayCircle sx={{ fontSize: 80, color: "white" }} />
              )}
            </IconButton>
          </Box>

          {/* Right side: Tabs with Close button */}
          <Box
            sx={{
              width: "30%",
              height: "100%",
              bgcolor: "#FFFFFF",
              boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              px: 2,
            }}
          >
            {/* Close button in top right corner */}
            <Box
              sx={{
                mt: 2,
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <IconButton onClick={handleCloseFullscreen}>
                <Close />
              </IconButton>
            </Box>
            {/* Tabs */}
            <Box sx={{ px: 2 }}>
              <Tabs
                value={modalTabIndex}
                onChange={(e, newValue) => setModalTabIndex(newValue)}
                sx={{
                  width: "100%",
                  "& .MuiTab-root": {
                    fontSize: 14,
                    p: 1,
                    "&.Mui-selected": {
                      color: "#9048FB",
                    },
                  },
                  "& .MuiTabs-indicator": {
                    bgcolor: "#9048FB",
                  },
                }}
              >
                <Tab
                  label="Follow-up Q&A"
                  sx={{ textTransform: "none", flex: 1, fontSize: 14 }}
                />
                <Tab
                  label="Transcript Auto Generated"
                  sx={{ textTransform: "none", flex: 1, fontSize: 14 }}
                />
              </Tabs>
            </Box>

            {/* Tab content */}
            <Box
              sx={{ px: 2, height: "calc(100% - 120px)", overflowY: "auto" }}
            >
              {modalTabIndex === 0 && (
                <Box sx={{ pt: 2, pb: 1 }}>
                  {/* <Typography variant="caption" color="text.secondary">
                    Auto-Generated
                  </Typography> */}
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      overflowY: "auto",
                      gap: 2,
                      p: 1,
                    }}
                  >
                    {followUpQnA && followUpQnA?.length > 0 ? (
                      followUpQnA
                        .filter(
                          (item) => !allQnaMessages.includes(item.question)
                        ) // 👈 filter out matching questions
                        .map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              flexDirection: "row", // Adjust layout to row for question and answer
                              alignItems: "flex-end",
                              gap: 3, // Adjust the gap between question and answer
                              borderBottom: "1px solid #e0e0e0", // Border between each QnA block
                              pb: 2,
                              "&:last-child": {
                                borderBottom: "none", // Remove border for last item
                              },
                            }}
                          >
                            {/* Question */}
                            {item.question && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flex: 1, // Allow the question to take up available space
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "black",
                                    width: 30,
                                    height: 30,
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  Q
                                </Avatar>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "#5A6E7E",
                                    direction: !shouldUseLTR ? "rtl" : "ltr",
                                    textAlign: !shouldUseLTR ? "right" : "left",
                                  }}
                                >
                                  {item.question}
                                </Typography>
                              </Box>
                            )}

                            {/* Answer */}
                            {item.answer && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: "#FFB400",
                                    width: 30,
                                    height: 30,
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  CR
                                </Avatar>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#5A6E7E",
                                    fontSize: "0.85rem",
                                    direction: !shouldUseLTR ? "rtl" : "ltr",
                                    textAlign: !shouldUseLTR ? "right" : "left",
                                  }}
                                >
                                  {item.answer}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{ color: "gray", textAlign: "center" }}
                      >
                        No follow-up questions and answers found.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {modalTabIndex === 1 && (
                <Box sx={{ pt: 2, pb: 1 }}>
                  {followUpQnA && followUpQnA?.length > 0 ? (
                    followUpQnA
                      ?.filter((item) => item.answer) // Show only answers
                      .map((item, index) => {
                        const itemTime = new Date(item.time).getTime();
                        const baseTime = new Date(startTimestamp).getTime();
                        const elapsedSeconds = Math.round(
                          (itemTime - baseTime) / 1000
                        );
                        const minutes = String(
                          Math.floor(elapsedSeconds / 60)
                        ).padStart(2, "0");
                        const seconds = String(elapsedSeconds % 60).padStart(
                          2,
                          "0"
                        );

                        return (
                          <Box
                            key={index}
                            sx={{
                              mt: 1,
                              mb: 1,
                              p: 1,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              borderBottom: "1px solid #e0e0e0",
                            }}
                          >
                            {/* Timestamp */}
                            <Typography
                              variant="caption"
                              sx={{
                                color: "gray",
                                fontSize: "0.85rem",
                                width: "40px",
                                textAlign: "right",
                                flexShrink: 0,
                                cursor: "pointer", // Make it look clickable
                                "&:hover": { color: "#9048FB" }, // Change color on hover
                              }}
                              onClick={() => {
                                if (modalVideoRef.current) {
                                  modalVideoRef.current.currentTime =
                                    elapsedSeconds; // Seek video to clicked timestamp
                                  modalVideoRef.current.play(); // Optionally start playback
                                }
                              }}
                            >
                              {minutes}:{seconds}
                            </Typography>

                            {/* Answer Text */}
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#2F4150",
                                fontSize: "0.85rem",
                                ml: 1,
                                flexGrow: 1,
                                direction: !shouldUseLTR ? "rtl" : "ltr",
                                textAlign: !shouldUseLTR ? "right" : "left",
                              }}
                            >
                              {item.answer}
                            </Typography>
                          </Box>
                        );
                      })
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{ color: "gray", textAlign: "center" }}
                    >
                      No transcription found
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const CandidateAssessmentQnaCard = ({
  language,
  qna,
  selectedAssessment,
  translate,
}) => {
  let isArabic = language === "Arabic"; // Check for Arabic language
  let shouldUseLTR = translate || !isArabic;
  let shouldUseTranslation = translate && !isArabic; // Add this for data selection

  console.log(
    "shouldUseLTR , isArabic",
    shouldUseLTR,
    isArabic,
    "shouldUseTranslation",
    shouldUseTranslation
  );

  const [expanded, setExpanded] = useState(true);
  const type = qna?.question_category;
  const [selectedTab, setSelectedTab] = useState(0); // State to manage selected tab

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue); // Update the selected tab
  };

  // Parse `answer_text` if it's a stringified array
  let parsedAnswerText = (() => {
    try {
      const sourceText = shouldUseTranslation
        ? qna?.translation?.answer_text
        : qna?.answer_text;
      const parsed = JSON.parse(sourceText);
      return Array.isArray(parsed) ? parsed[0] : sourceText;
    } catch (error) {
      return shouldUseTranslation
        ? qna?.translation?.answer_text
        : qna?.answer_text;
    }
  })();

  let isCandidateAnswerCorrect =
    parsedAnswerText ===
    (shouldUseTranslation
      ? qna?.translation?.expected_answer
      : qna?.expected_answer);
  let answer_text = qna?.id ? parsedAnswerText : parsedAnswerText;
  let question_text = shouldUseTranslation
    ? qna?.translation?.message
    : qna?.message;

  // Calculate the score percentage for the progress bars
  const getScorePercentage = (score) => {
    return `${score}%`;
  };

  // Convert object into an array of objects with color mapping
  const metrics = qna?.question_behaviour_score
    ? Object.entries(qna.question_behaviour_score).map(([key, value]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
        value: parseFloat(value).toFixed(2), // Ensure value is a number before calling toFixed()
        color: metricColors[key] || "#ccc", // Fallback color
      }))
    : []; // Return an empty array if data is unavailable

  // Function to transform the soft_skills data into the format needed for your component
  const mapSoftSkillsData = (resultAnalysis) => {
    if (!resultAnalysis || !resultAnalysis.soft_skills) {
      return [];
    }

    const softSkillsData = resultAnalysis.soft_skills;

    // Map each key in soft_skills to the format { label: "Key Name", value: score }
    return Object.entries(softSkillsData).map(([key, data]) => {
      // Convert snake_case to Title Case for display
      const label = key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      return {
        label,
        value: data.score,
        reason: data.reason, // You can include the reason if you want to display it
      };
    });
  };

  // Map the soft skills for your component
  const softSkills = mapSoftSkillsData(qna?.result_analysis);

  // Similarly for technical skills
  const mapTechnicalSkillsData = (resultAnalysis) => {
    if (!resultAnalysis || !resultAnalysis?.technical_skills) {
      return [];
    }

    const technicalSkillsData = resultAnalysis?.technical_skills;

    return Object.entries(technicalSkillsData).map(([key, data]) => {
      // Handle the special case structure for accuracy_of_response
      const score =
        typeof data === "object" && "score" in data
          ? data.score
          : typeof data === "object" && data.score !== undefined
          ? data.score
          : 0;

      // Convert snake_case to Title Case
      const label = key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      return {
        label,
        value: score,
        reason: typeof data === "object" && "reason" in data ? data.reason : "",
      };
    });
  };

  const technicalSkills = mapTechnicalSkillsData(qna?.result_analysis);

  // Set the tab based on available data
  useEffect(() => {
    if (technicalSkills?.length > 0) {
      setSelectedTab(0); // Prioritize Technical Skills
    } else if (softSkills?.length > 0) {
      setSelectedTab(1); // Only Soft Skills available
    }
  }, []);

  const SkillBar = ({ label, value, color }) => (
    <Box mb={2}>
      <Box display="flex" alignItems="center" gap={2} mb={0.5}>
        <Typography
          variant="body2"
          color="textPrimary"
          width={120}
          fontFamily="Outfit"
        >
          {label}
        </Typography>
        {/* Progress Bar Wrapper with width */}
        <Box flex={1}>
          <LinearProgress
            variant="determinate"
            value={value}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: "#E0E0E0",
              "& .MuiLinearProgress-bar": { backgroundColor: color },
            }}
          />
        </Box>
        <Typography variant="body2" color="textPrimary" fontFamily="Outfit">
          {Number(value)?.toFixed(1)}%
        </Typography>
      </Box>
    </Box>
  );

  const SkillSection = ({ title, skills, color }) => (
    <Box mb={4}>
      <Box mt={4}>
        {skills?.map((skill) => (
          <SkillBar
            key={skill.label}
            label={skill.label}
            value={skill.value}
            color={color}
          />
        ))}
      </Box>
    </Box>
  );

  const hideExtraInfo =
    qna?.introductory_message?.toString().trim() === "true" ||
    qna?.last_message?.toString().trim() === "true";

  const safeParseFollowUpQnA = (followUpString) => {
    try {
      // Check if it's already an object
      if (typeof followUpString === "object" && followUpString !== null) {
        return Array.isArray(followUpString)
          ? followUpString
          : [followUpString];
      }

      // Handle null, undefined, or empty string
      if (!followUpString || followUpString.trim() === "") {
        return [];
      }

      // Convert to string and clean
      let cleanedString = String(followUpString).trim();

      // Remove outer quotes if present
      if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
        cleanedString = cleanedString.slice(1, -1);
      }

      // Handle escaped quotes within the string
      cleanedString = cleanedString.replace(/\\"/g, '"');

      // Try direct JSON parse first
      try {
        const parsed = JSON.parse(cleanedString);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (firstError) {
        console.log("First parse attempt failed:", firstError.message);

        // Try fixing JSON issues with smarter quote replacement
        let fixedString = cleanedString
          // Replace single quotes with double quotes only when they're used as string delimiters
          .replace(/'([^']+)'(?=\s*[:,\]}])/g, '"$1"') // End quotes before :,]}
          .replace(/([{:,\[]\s*)'([^']+)'/g, '$1"$2"') // Start quotes after {:[
          // Fix missing quotes around keys (basic pattern)
          .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
          // Fix trailing commas
          .replace(/,(\s*[}\]])/g, "$1")
          // Fix missing commas between objects/arrays
          .replace(/}(\s*){/g, "},$1{")
          .replace(/](\s*)\[/g, "],$1[");

        try {
          const parsed = JSON.parse(fixedString);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (secondError) {
          console.log("Second parse attempt failed:", secondError.message);

          // Try even more aggressive cleaning
          try {
            // Remove any control characters and fix newlines
            let aggressiveClean = fixedString
              .replace(/[\n\r\t]/g, " ")
              .replace(/\s+/g, " ")
              .trim();

            const parsed = JSON.parse(aggressiveClean);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch (thirdError) {
            console.log("Third parse attempt failed:", thirdError.message);

            // Last resort: try to extract JSON-like patterns
            try {
              // Look for array patterns [...]
              const arrayMatch = cleanedString.match(/\[.*\]/s);
              if (arrayMatch) {
                const parsed = JSON.parse(arrayMatch[0]);
                return Array.isArray(parsed) ? parsed : [parsed];
              }

              // Look for object patterns {...}
              const objectMatch = cleanedString.match(/\{.*\}/s);
              if (objectMatch) {
                const parsed = JSON.parse(objectMatch[0]);
                return Array.isArray(parsed) ? [parsed] : parsed;
              }
            } catch (extractError) {
              console.log("Pattern extraction failed:", extractError.message);
            }
          }
        }
      }
    } catch (error) {
      console.error("All parsing attempts failed:", error);
      console.log("Original string type:", typeof followUpString);
      console.log(
        "Original string (first 500 chars):",
        String(followUpString).substring(0, 500)
      );
      console.log(
        "Original string (last 100 chars):",
        String(followUpString).slice(-100)
      );

      // Try to identify the problematic character around position 397
      const str = String(followUpString);
      if (str.length > 397) {
        console.log("Context around position 397:", str.substring(390, 410));
      }

      return [];
    }
  };

  const cleanFollowUp = safeParseFollowUpQnA(
    shouldUseTranslation ? qna?.translation?.followUpQnA : qna?.followUpQnA
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 2,
        bgcolor: "#ffffff",
        borderRadius: 2,
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Intro Question Section - Only show when introductory message exists */}
      {qna?.introductory_message === "true" && (
        <Box
          sx={{
            borderLeft: "4px solid #FFC107", // Yellow border like in your example
            bgcolor: "#FFF8E1", // Light yellow background
            pl: 2,
            py: 1.5,
            mb: 3,
            mx: "40px",
            borderRadius: "4px",
          }}
        >
          <Typography
            fontWeight={600}
            fontFamily="Outfit"
            sx={{ fontSize: "1rem", color: "#494F5E" }}
          >
            Intro Question
          </Typography>
          <Typography
            fontFamily="Outfit"
            sx={{ fontSize: "0.875rem", color: "#303030", mt: 0.5 }}
          >
            This question is placed at the beginning of the assessment to help
            candidates settle in and express their initial thoughts or
            motivation. It offers early insight into their mindset.
          </Typography>
        </Box>
      )}

      {/* Outri Question Section - Only show when last message exists */}
      {qna?.last_message?.toString().trim() === "true" && (
        <Box
          sx={{
            borderLeft: "4px solid #FFC107", // Yellow border like in your example
            bgcolor: "#FFF8E1", // Light yellow background
            pl: 2,
            py: 1.5,
            mb: 3,
            mx: "40px",
            borderRadius: "4px",
          }}
        >
          <Typography
            fontWeight={600}
            fontFamily="Outfit"
            sx={{ fontSize: "1rem", color: "#494F5E" }}
          >
            Outro
          </Typography>
          <Typography
            fontFamily="Outfit"
            sx={{ fontSize: "0.875rem", color: "#303030", mt: 0.5 }}
          >
            This is a closing message shown at the end of the assessment to wrap
            up the experience.
          </Typography>
        </Box>
      )}

      {/* Header with Suggested and Rating */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          justifyContent: "space-between",
          mx: "40px",
        }}
      >
        {!hideExtraInfo ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="img"
              src={Logo}
              sx={{ width: "19px", height: "25px" }}
            />
            {qna?.suggested && (
              <Typography
                fontWeight={600}
                sx={{ fontSize: "0.95rem" }}
                fontFamily="Outfit"
              >
                Suggested
              </Typography>
            )}
            <QnaRating value={(qna?.answer_score / 100) * 5} />
          </Box>
        ) : (
          <Box />
        )}{" "}
        {/* Add placeholder to maintain spacing if needed */}
        {/* Always show expanded icon + optional info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!hideExtraInfo && (
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                sx={{
                  width: "24px",
                  height: "24px",
                  border: "1px solid #d1d1d1",
                }}
              >
                <HelpCenterOutlined
                  sx={{ width: "14px", height: "14px", color: "#999" }}
                />
              </IconButton>
              <Typography fontFamily="Outfit" fontSize="12px" noWrap>
                {getCamelCaseWord(qna?.question_category)}
              </Typography>

              <IconButton
                size="small"
                sx={{
                  width: "24px",
                  height: "24px",
                  border: "1px solid #d1d1d1",
                }}
              >
                <TimerOutlined
                  sx={{ width: "14px", height: "14px", color: "#999" }}
                />
              </IconButton>
              <Typography fontFamily="Outfit" fontSize="12px" noWrap>
                {qna?.test_time}
              </Typography>
            </Box>
          )}
          <IconButton
            size="small"
            sx={{ color: "#666" }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {expanded && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Grid
              container
              spacing={8}
              sx={{
                mx: "auto",
              }}
            >
              <Grid item xs={12} sm={12} md={7}>
                {/* Question */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{ bgcolor: "black", width: 36, height: 36, mr: 2 }}
                  >
                    Q
                  </Avatar>
                  <Typography
                    sx={{
                      color: "#333333",
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                      flexGrow: 1,
                      p: 1,
                      py: 2,
                      bgcolor: "#F9F9F957",
                      borderRadius: 2,
                      border: "1px solid #F1F1F1",
                      fontFamily: "Outfit",
                      direction: !shouldUseLTR ? "rtl" : "ltr",
                      textAlign: !shouldUseLTR ? "right" : "left",
                    }}
                  >
                    {question_text}
                  </Typography>
                </Box>
                {/* Expected Answer */}

                {type && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "#4CC4FF",
                          width: 36,
                          height: 36,
                          mr: 2,
                        }}
                      >
                        EA
                      </Avatar>
                      {type !== "multiple choice" ? (
                        <Typography
                          sx={{
                            color: "#333333",
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                            flexGrow: 1,
                            p: 1,
                            py: 2,
                            bgcolor: "#F9F9F957",
                            borderRadius: 2,
                            border: "1px solid #F1F1F1",
                            fontFamily: "Outfit",
                            direction: !shouldUseLTR ? "rtl" : "ltr",
                            textAlign: !shouldUseLTR ? "right" : "left",
                          }}
                        >
                          {qna?.expected_answer
                            ? shouldUseTranslation
                              ? qna?.translation?.expected_answer
                              : qna?.expected_answer
                            : "No answer provided."}
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            p: 1,
                            py: 2,
                            bgcolor: "#F9F9F957",
                            borderRadius: 2,
                            border: "1px solid #F1F1F1",
                            fontFamily: "Outfit",
                          }}
                        >
                          {shouldUseTranslation
                            ? qna?.translation?.options
                            : qna?.options?.map((option, index) => {
                                const letter = String.fromCharCode(65 + index);
                                const isExpectedAnswer = shouldUseTranslation
                                  ? qna?.translation?.expected_answer
                                  : qna?.expected_answer?.includes(option);
                                const isUserAnswer =
                                  option?.trim()?.toLowerCase() ===
                                  (shouldUseTranslation
                                    ? qna?.translation?.answer_text
                                    : qna?.answer_text
                                  )
                                    ?.trim()
                                    ?.toLowerCase()
                                    ?.replace('["', "")
                                    .replace('"]', "");
                                const isCorrectAnswer =
                                  isUserAnswer && isExpectedAnswer;

                                let bgColor = "transparent";
                                if (isCorrectAnswer) {
                                  bgColor = "rgba(52, 214, 79, 0.1)"; // green
                                } else if (isUserAnswer && !isExpectedAnswer) {
                                  bgColor = "rgba(255, 0, 0, 0.1)"; // red
                                } else if (isExpectedAnswer) {
                                  bgColor = "rgba(76, 196, 255, 0.1)"; // blue
                                }

                                return (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      p: 1,
                                      borderRadius: 1,
                                      mb: 1,
                                      bgcolor: bgColor,
                                      borderWidth: 1,
                                      borderStyle: "solid",
                                      borderColor: bgColor,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        minWidth: 30,
                                        fontWeight: 500,
                                        color: "#555",
                                        fontSize: "0.85rem",
                                        direction: !shouldUseLTR
                                          ? "rtl"
                                          : "ltr", // Keep shouldUseLTR for styling
                                        textAlign: !shouldUseLTR
                                          ? "right"
                                          : "left", // Keep shouldUseLTR for styling
                                      }}
                                    >
                                      {letter}:
                                    </Typography>
                                    <Typography
                                      sx={{
                                        flex: 1,
                                        color: "#2C3E50",
                                        fontSize: "0.85rem",
                                        direction: !shouldUseLTR
                                          ? "rtl"
                                          : "ltr", // Keep shouldUseLTR for styling
                                        textAlign: !shouldUseLTR
                                          ? "right"
                                          : "left", // Keep shouldUseLTR for styling
                                      }}
                                    >
                                      {option}
                                    </Typography>
                                    {isExpectedAnswer && (
                                      <Check sx={{ color: "#4CC4FF", ml: 1 }} />
                                    )}
                                  </Box>
                                );
                              })}
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {/* Candidate Answer */}
                {type && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "#FFB400",
                          width: 36,
                          height: 36,
                          mr: 2,
                        }}
                      >
                        CR
                      </Avatar>
                      {type !== "multiple choice" ? (
                        <Typography
                          sx={{
                            color: "#333333",
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                            flexGrow: 1,
                            p: 1,
                            py: 2,
                            bgcolor: "#F9F9F957",
                            borderRadius: 2,
                            border: "1px solid #F1F1F1",
                            fontFamily: "Outfit",
                            direction: !shouldUseLTR ? "rtl" : "ltr",
                            textAlign: !shouldUseLTR ? "right" : "left",
                          }}
                        >
                          {answer_text ? answer_text : "No answer provided."}
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            p: 1,
                            py: 2,
                            bgcolor: "#F9F9F957",
                            borderRadius: 2,
                            border: "1px solid #F1F1F1",
                            fontFamily: "Outfit",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              p: 1,
                              borderRadius: 1,
                              bgcolor: isCandidateAnswerCorrect
                                ? "rgba(76, 196, 255, 0.1)"
                                : "#FEDBDB",
                              width: "100%",
                              borderWidth: 1,
                              borderStyle: "solid",
                              borderColor: isCandidateAnswerCorrect
                                ? "rgba(76, 196, 255, 0.1)"
                                : "#FEDBDB",
                            }}
                          >
                            <Typography
                              sx={{
                                flex: 1,
                                fontSize: "0.85rem",
                                color: isCandidateAnswerCorrect
                                  ? "#2C3E50"
                                  : "#FF2D2D",
                                direction: !shouldUseLTR ? "rtl" : "ltr",
                                textAlign: !shouldUseLTR ? "right" : "left",
                              }}
                            >
                              {answer_text
                                ? answer_text
                                : "No answer provided."}
                            </Typography>
                            {isCandidateAnswerCorrect ? (
                              <Check sx={{ color: "#4CC4FF", ml: 1 }} />
                            ) : (
                              <Close sx={{ color: "red", ml: 1 }} />
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                {/* Video Player */}
                <Box sx={{ mt: 4, position: "relative", width: "100%" }}>
                  <VideoPlayer2
                    videoUrl={qna?.answer_video}
                    expanded={expanded}
                    transcription={
                      shouldUseLTR
                        ? qna?.translation?.answer_transcription
                        : qna?.answer_transcription
                    }
                    qna={qna}
                    followUpQnA={cleanFollowUp}
                    language={language}
                    shouldUseLTR={shouldUseLTR}
                    selectedAssessment={selectedAssessment}
                  />
                </Box>
              </Grid>

              {/* Analysis Column */}
              <Grid item xs={12} sm={12} md={5}>
                <Box sx={{ px: { xs: 2, md: 2 } }}>
                  {metrics?.length > 0 ? (
                    <Card
                      sx={{
                        bgcolor: "#FBFBFB",
                        px: { xs: 2, md: 5 },
                        my: 3,
                        py: 3,
                        border: 1,
                        borderColor: "#DDE3E5",
                        borderRadius: "10px",
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                        Behaviors Analysis
                      </Typography>

                      {/* Progress bars for behaviors */}
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: 2,
                            overflow: "auto",
                          }}
                        >
                          {metrics.map((metric) => (
                            <Box
                              key={metric?.name}
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                width: "100%",
                                gap: 2,
                              }}
                            >
                              <Typography
                                sx={{
                                  width: 120,
                                  textAlign: { xs: "left", sm: "left" },
                                }}
                                variant="body2"
                              >
                                {metric?.name}
                              </Typography>
                              <SegmentedProgressBar
                                value={metric?.value}
                                color={metric?.color}
                                qna={true}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Card>
                  ) : (
                    // Show Skeleton Loader when no data is available
                    <Card
                      sx={{
                        bgcolor: "#FBFBFB",
                        px: { xs: 2, md: 5 },
                        py: 3,
                        my: 3,
                        border: 1,
                        borderColor: "#DDE3E5",
                        borderRadius: "10px",
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                        Behaviors Analysis
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: 2,
                            overflow: "auto",
                          }}
                        >
                          {/* Skeleton Loaders for Metrics */}
                          {[...Array(5)].map((_, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                width: "100%",
                                gap: 2,
                              }}
                            >
                              <Skeleton
                                variant="text"
                                width={120}
                                height={20}
                                sx={{ bgcolor: "#ddd" }}
                              />
                              <Skeleton
                                variant="rectangular"
                                width="80%"
                                height={10}
                                sx={{ bgcolor: "#ddd", borderRadius: "4px" }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Card>
                  )}

                  {type === "multiple choice" ? (
                    <Card
                      sx={{
                        bgcolor: "#FBFBFB",
                        px: { xs: 2, md: 5 },
                        mt: 4,
                        py: 3,
                        border: 1,
                        borderColor: "#DDE3E5",
                        borderRadius: "10px",
                        position: "relative",
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                        Accuracy Analysis{" "}
                      </Typography>

                      {/* Progress bars for behaviors */}
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: 2,
                            overflow: "auto",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              alignItems: { xs: "flex-start", sm: "center" },
                              width: "100%",
                              gap: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                width: 120,
                                textAlign: { xs: "left", sm: "left" },
                              }}
                              variant="body2"
                            >
                              Response Accuracy
                            </Typography>
                            <SegmentedProgressBar
                              value={qna?.answer_score}
                              color="#A358E9"
                              qna={true}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  ) : (
                    <Card
                      sx={{
                        bgcolor: "#FBFBFB",
                        px: { xs: 2, md: 5 },
                        py: 3,
                        mt: 4,
                        border: 1,
                        borderColor: "#DDE3E5",
                        borderRadius: "10px",
                        position: "relative",
                      }}
                    >
                      <Typography variant="h6" color="textPrimary" mb={3}>
                        Result Analysis
                      </Typography>

                      {/* Tabs for Technical Skills and Soft Skills */}

                      <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        sx={{
                          "& .MuiTab-root": {
                            pb: 1.5,
                            fontFamily: "Outfit",
                            fontSize: { xs: "12px", sm: "15px", md: "16px" },
                            "&.Mui-selected": {
                              color: selectedTab === 0 ? "#9048FB" : "#26C6DA",
                              fontFamily: "Outfit",
                            },
                          },
                          "& .MuiTabs-indicator": {
                            bgcolor: selectedTab === 0 ? "#9048FB" : "#26C6DA",
                          },
                        }}
                      >
                        <Tab
                          label="Technical Skills"
                          sx={{ textTransform: "none", fontSize: 14 }}
                        />
                        <Tab
                          label="Soft Skills"
                          sx={{ textTransform: "none", fontSize: 14 }}
                        />
                      </Tabs>

                      {/* Tab Content */}
                      {selectedTab === 0 && (
                        <Box>
                          {technicalSkills && technicalSkills.length > 0 ? (
                            <SkillSection
                              title="Technical Skills"
                              skills={technicalSkills}
                              color="#7E57C2"
                            />
                          ) : (
                            <Box
                              sx={{
                                mt: 5,
                                mb: 4,
                                textAlign: "center",
                                fontFamily: "Outfit",
                                color: "text.secondary",
                              }}
                            >
                              <Typography>
                                No technical skills data found
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {selectedTab === 1 && (
                        <Box>
                          {softSkills && softSkills.length > 0 ? (
                            <SkillSection
                              title="Soft Skills"
                              skills={softSkills}
                              color="#26C6DA"
                            />
                          ) : (
                            <Box
                              sx={{
                                mt: 5,
                                mb: 4,
                                textAlign: "center",
                                color: "text.secondary",
                                fontFamily: "Outfit",
                              }}
                            >
                              <Typography>No soft skills data found</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Card>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      {/* Match Score */}
      {type && expanded && (
        <Box
          sx={{
            py: 2,
            px: 2,
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            borderTop: "2px solid",
            borderColor: "#DBEAFE",
            bgcolor: "#944CFF",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#FFFFFF", fontFamily: "Outfit" }}
          >
            Question match score
          </Typography>
          {/* Progress Bar Wrapper with width */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Box sx={{ width: 250 }}>
              {/* <-- FIXED WIDTH ADDED */}
              <LinearProgress
                variant="determinate"
                value={Math.round(qna?.answer_score) || 0}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "#E0E0E0",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#4BC7E1" },
                }}
              />
            </Box>
            <Typography color="#FFFFFF" fontWeight={500}>
              {Math.round(qna?.answer_score) || 0}%
            </Typography>
          </Box>
        </Box>
      )}

      {!expanded && (
        <>
          <Grid
            container
            spacing={4}
            sx={{
              mx: "auto",
              alignItems: "flex-start",
            }}
          >
            <Grid item xs={12} sm={12} md={7}>
              {/* Question */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "black", width: 36, height: 36, mr: 2 }}>
                  Q
                </Avatar>
                <Typography
                  sx={{
                    color: "#333333",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                    flexGrow: 1,
                    p: 1,
                    py: 2,
                    bgcolor: "#F9F9F957",
                    borderRadius: 2,
                    border: "1px solid #F1F1F1",
                    fontFamily: "Outfit",
                  }}
                >
                  {question_text}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={5}>
              <Box
                sx={{
                  px: { xs: 2, md: 5 },
                }}
              >
                {/* Progress bars for behaviors */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      overflow: "auto",
                    }}
                  >
                    {metrics?.length > 0 &&
                      (() => {
                        const highestMetric = metrics.reduce(
                          (max, metric) =>
                            metric?.value > (max?.value || 0) ? metric : max,
                          {}
                        );

                        return (
                          <Box
                            key={highestMetric?.name}
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              alignItems: { xs: "flex-start", sm: "center" },
                              width: "100%",
                              gap: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                width: 120, // Ensures proper spacing on larger screens
                                textAlign: { xs: "left", sm: "left" },
                                color: "text.secondary",
                                fontFamily: "Outfit",
                              }}
                              variant="body2"
                            >
                              {highestMetric?.name}
                            </Typography>
                            <SegmentedProgressBar
                              value={highestMetric?.value}
                              color={highestMetric?.color}
                              qna={true}
                            />
                          </Box>
                        );
                      })()}
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontWeight: 500,
                        color: "#3D4A55",
                        fontFamily: "Outfit",
                      }}
                    >
                      Highest score in behavior
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
};

export default CandidateAssessmentQnaCard;
